import { FC, useContext, useState } from 'react';

import { YourWatch } from 'components/common/summary/YourWatch';
import { CurrencyInput } from 'components/design-system/CurrencyInput';
import { Slideout, SlideoutCommonProps, SubmitButtonWithArrow } from 'components/design-system/Slideout';
import { useModal } from 'context/modal/ModalContext';
import { SellerProfileContext } from 'context/sellerProfile';
import { useAutoFocus } from 'hooks/useAutoFocus';
import { Listing } from 'types';
import { formatPrice, formatPriceFromDollars, getListingBreakdownPrice } from 'utils/text-helpers';

import { getTexts } from './getTexts';
import { updateReserve } from './updateReserve';

import { AuctionSubmittedSlideout } from '../AuctionSubmittedSlideout';
import { duplicateListing } from '../duplicateListing';
import { WatchHeader } from '../WatchHeader';

type SetReserveSlideoutProps = {
  listing: Listing;
  refreshListing?: (listing: Listing) => void;
  isUpdating?: boolean;
  currentReserve?: number | null;
  canOnlyReduce?: boolean;
} & SlideoutCommonProps;

export const SetReserveSlideout: FC<SetReserveSlideoutProps> = ({
  listing,
  isUpdating,
  refreshListing,
  currentReserve,
  canOnlyReduce = false,
  ...props
}) => {
  const initialReserve = currentReserve ? String(currentReserve / 100) : undefined;
  const [reserve, setReserve] = useState<string | undefined>(currentReserve ? initialReserve : '');
  const [isUpdateLoading, setIsUpdateLoading] = useState(false);
  const [isRemoveLoading, setIsRemoveLoading] = useState(false);
  const { autoFocus, enableAutoFocus } = useAutoFocus();

  const { closeModal, openModal } = useModal();

  const openAuctionSubmittedSlideout = () => openModal(<AuctionSubmittedSlideout closeModal={closeModal} />);

  const reserveCents = Number(reserve || 0) * 100;

  const submitAction = () =>
    isUpdating
      ? updateReserve({
          setIsLoading: setIsUpdateLoading,
          listing,
          newReserveValue: reserveCents,
          refreshListing,
          closeModal
        })
      : duplicateListing(setIsUpdateLoading, listing.id, { reserveCents }, openAuctionSubmittedSlideout);
  const clearReserve = () =>
    updateReserve({ setIsLoading: setIsRemoveLoading, listing, newReserveValue: null, refreshListing, closeModal });

  const { sellerProfile, sellerLevels } = useContext(SellerProfileContext);

  const { commissionCents, earnings, sellerFeeCents } = getListingBreakdownPrice(
    {
      ...listing,
      priceCents: reserveCents
    },
    sellerProfile,
    sellerLevels
  );

  const { submitButtonText, title } = getTexts({ canOnlyReduce, isUpdating });
  const hasError = !!canOnlyReduce && !!currentReserve && reserveCents > currentReserve;
  const userSellerLevel = sellerLevels?.find(({ level }) => level === sellerProfile?.sellerLevel);
  const commission = (userSellerLevel?.commissionRateBips || 0) / 100;

  return (
    <Slideout
      onShow={enableAutoFocus}
      {...props}
      cancel={
        isUpdating && !!currentReserve
          ? {
              onClick: clearReserve,
              title: 'Remove the reserve',
              loading: isRemoveLoading,
              disabled: isRemoveLoading || isUpdateLoading
            }
          : undefined
      }
      submit={{
        loading: isUpdateLoading,
        onClick: reserveCents === 0 ? clearReserve : submitAction,
        textClassname: 'w-100',
        disabled:
          !reserve || isUpdateLoading || isRemoveLoading || (isUpdating && initialReserve === reserve) || hasError,
        title: <SubmitButtonWithArrow text={submitButtonText} />
      }}
      customHeader={({ onClickPrevious }) => (
        <WatchHeader
          onClickPrevious={isUpdating ? undefined : onClickPrevious}
          listing={listing}
          onClose={closeModal}
        />
      )}
    >
      <div className='riforma-regular'>
        <p className='fs-24px mt-4 text-center'>{title}</p>
        <p className='opacity-60 mb-5 px-4 text-center'>
          Choose the amount you’d like to{' '}
          {isUpdating ? 'set the price the watch has to hit to sell' : 'be paid for the watch'}
        </p>
        <CurrencyInput
          autoFocus={autoFocus}
          isError={hasError}
          placeholder='Your reserve'
          className='w-100 mb-3 rounded-2 py-3 px-4 border-success'
          prefix='$ '
          onValueChange={setReserve}
          style={{ border: '1px solid' }}
          value={reserve}
        />
        {hasError && (
          <div className='text-danger fs-14px'>It is only possible to reduce the reserve for live auctions</div>
        )}
        <YourWatch listing={listing} />
        <div className='border-top my-3' />
        <div className='d-flex justify-content-between fs-14px'>
          <span className='opacity-90'>Reserve Price</span>
          <span>{formatPriceFromDollars(Number(reserve || 0))}</span>
        </div>
        <div className='d-flex justify-content-between fs-14px opacity-60'>
          <span>
            Tier {sellerProfile?.sellerLevel} Commission ({commission}%)
          </span>
          <span>{formatPrice(commissionCents - sellerFeeCents)}</span>
        </div>
        <div className='d-flex justify-content-between fs-14px opacity-60'>
          <span>Seller fee</span>
          <span>{formatPrice(sellerFeeCents)}</span>
        </div>
        <div className='border-top my-3' />
        <div className='d-flex justify-content-between fs-16px riforma-medium'>
          <span>Your payout</span>
          <span className='text-success'>{formatPrice(earnings)}</span>
        </div>
      </div>
    </Slideout>
  );
};
